import React, { useState, useEffect } from "react";
import MasterLayout from "../Sections/MasterLayout";
import SuccesModal from "../Sections/SuccesModal";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Loader from "./Loader";
import {
  save_blog,
  get_blog,
  server_post_data,
} from "../../ServiceConnection/serviceconnection";
import { useNavigate, useParams } from "react-router-dom";

export default function AddBlog() {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();

  // Original blog data from API
  const [blogData, setBlogData] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    author: "",
    shortDesc: "",
    fullDesc: "",
    coverImg: null,
    blogImg: null,
  });

  const [previewCover, setPreviewCover] = useState(null);
  const [previewBlog, setPreviewBlog] = useState(null);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Validation
  const validateForm = () => {
    let newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.subtitle.trim()) newErrors.subtitle = "Subtitle is required";
    if (!formData.author.trim()) newErrors.author = "Author name is required";
    if (!formData.shortDesc.trim())
      newErrors.shortDesc = "Short description is required";
    if (!formData.fullDesc.trim())
      newErrors.fullDesc = "Full description is required";
    if (!previewCover) newErrors.coverImg = "Cover image is required";
    if (!previewBlog) newErrors.blogImg = "Blog image is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Image Handler
  const handleImage = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "coverImg") setPreviewCover(reader.result);
      if (type === "blogImg") setPreviewBlog(reader.result);
    };
    reader.readAsDataURL(file);

    setFormData({ ...formData, [type]: file });
    setErrors((p) => ({ ...p, [type]: "" }));
  };

  // Remove Image
  const removeImage = (type) => {
    if (type === "coverImg") {
      setPreviewCover(null);
      setFormData({ ...formData, coverImg: null });
    }
    if (type === "blogImg") {
      setPreviewBlog(null);
      setFormData({ ...formData, blogImg: null });
    }
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setSuccess(false);

    try {
      const fd = new FormData();
      if (isEditMode) fd.append("primary_id", id);

      fd.append("title", formData.title);
      fd.append("subtitle", formData.subtitle);
      fd.append("author", formData.author);
      fd.append("shortDesc", formData.shortDesc);
      fd.append("fullDesc", formData.fullDesc);

      if (formData.coverImg instanceof File) fd.append("coverImg", formData.coverImg);
      if (formData.blogImg instanceof File) fd.append("blogImg", formData.blogImg);

      const response = await server_post_data(save_blog, fd);
      const payload = response?.data;

      if (payload?.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/blogs");
        }, 1500);
      } else {
        setErrors({ general: payload?.message || "Failed to save blog" });
      }
    } catch (error) {
      console.error("Save error:", error);
      setErrors({ general: "Server error. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  // Load blog data (edit mode)
  const loadBlogData = async () => {
    try {
      setIsLoading(true);
      const fd = new FormData();
      fd.append("primary_id", id);
      const response = await server_post_data(get_blog, fd);
      const data = response.data?.data || {};

      setBlogData(data);

      if (data.coverImgFull) setPreviewCover(data.coverImgFull);
      if (data.blogImgFull) setPreviewBlog(data.blogImgFull);
    } catch (error) {
      console.error("Load blog error:", error);
      setErrors({ general: "Failed to load blog data" });
    } finally {
      setIsLoading(false);
    }
  };

  // Sync form fields when blogData loads
  useEffect(() => {
    if (!isEditMode || !blogData) return;

    setFormData((prev) => ({
      ...prev,
      title: blogData.title || "",
      subtitle: blogData.subtitle || "",
      author: blogData.author || "",
      shortDesc: blogData.shortDesc || "",
      fullDesc: blogData.fullDesc || "",
    }));
  }, [blogData, isEditMode]);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      if (isEditMode) {
        await loadBlogData();
      }

      setIsLoading(false);
    };

    init();
  }, [id, isEditMode]);

  return (
    <MasterLayout>
      {isLoading && <Loader />}
      <div className="mx-auto bg-white p-6 rounded-lg shadow border">
        <h2 className="text-xl font-semibold mb-5">
          {isEditMode ? "Update Blog" : "Add New Blog"}
        </h2>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Images Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cover Image */}
            <div>
              <label className="block font-semibold text-gray-700 mb-3">
                Cover Image (1400×400)
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors bg-gray-50">
                {previewCover ? (
                  <div className="relative group">
                    <img
                      src={previewCover}
                      alt="Cover Preview"
                      className="w-full h-48 object-cover rounded-lg shadow-md"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 rounded-lg flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeImage("coverImg")}
                        className="opacity-0 group-hover:opacity-100 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg shadow-lg transform transition-all duration-200 font-medium"
                      >
                        Remove Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-48 cursor-pointer">
                    <svg
                      className="w-12 h-12 text-gray-400 mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <span className="text-sm text-gray-600 font-medium mb-1">
                      Click to upload cover image
                    </span>
                    <span className="text-xs text-gray-500">
                      PNG, JPG up to 1MB (1400×400 recommended)
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImage(e, "coverImg")}
                    />
                  </label>
                )}
              </div>
              {errors.coverImg && (
                <p className="text-red-600 text-sm mt-2 flex items-center">
                  <span className="mr-1">Warning</span>
                  {errors.coverImg}
                </p>
              )}
            </div>

            {/* Blog Image */}
            <div>
              <label className="block font-semibold text-gray-700 mb-3">
                Blog Image (400×350)
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors bg-gray-50">
                {previewBlog ? (
                  <div className="relative group">
                    <img
                      src={previewBlog}
                      alt="Blog Preview"
                      className="w-full h-48 object-cover rounded-lg shadow-md"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 rounded-lg flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeImage("blogImg")}
                        className="opacity-0 group-hover:opacity-100 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg shadow-lg transform transition-all duration-200 font-medium"
                      >
                        Remove Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-48 cursor-pointer">
                    <svg
                      className="w-12 h-12 text-gray-400 mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-sm text-gray-600 font-medium mb-1">
                      Click to upload blog image
                    </span>
                    <span className="text-xs text-gray-500">
                      PNG, JPG up to 1MB (400×350 recommended)
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImage(e, "blogImg")}
                    />
                  </label>
                )}
              </div>
              {errors.blogImg && (
                <p className="text-red-600 text-sm mt-2 flex items-center">
                  <span className="mr-1">Warning</span>
                  {errors.blogImg}
                </p>
              )}
            </div>
          </div>

          {/* Title + Subtitle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="font-semibold text-gray-700">Title</label>
              <input
                className="w-full border p-3 rounded-lg mt-1"
                placeholder="Enter blog title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
              {errors.title && (
                <p className="text-red-600 text-sm mt-1">{errors.title}</p>
              )}
            </div>
            <div>
              <label className="font-semibold text-gray-700">Subtitle</label>
              <input
                className="w-full border p-3 rounded-lg mt-1"
                placeholder="Enter subtitle"
                value={formData.subtitle}
                onChange={(e) =>
                  setFormData({ ...formData, subtitle: e.target.value })
                }
              />
              {errors.subtitle && (
                <p className="text-red-600 text-sm mt-1">{errors.subtitle}</p>
              )}
            </div>
          </div>

          {/* Author */}
          <div>
            <label className="font-semibold text-gray-700">Author Name</label>
            <input
              className="w-full border p-3 rounded-lg mt-1"
              placeholder="Enter author name"
              value={formData.author}
              onChange={(e) =>
                setFormData({ ...formData, author: e.target.value })
              }
            />
            {errors.author && (
              <p className="text-red-600 text-sm mt-1">{errors.author}</p>
            )}
          </div>

          {/* Short Description */}
          <div>
            <label className="font-semibold text-gray-700">
              Short Description
            </label>
            <textarea
              className="w-full border p-3 rounded-lg mt-1"
              rows={3}
              placeholder="Short summary (shown on listing page)"
              value={formData.shortDesc}
              onChange={(e) =>
                setFormData({ ...formData, shortDesc: e.target.value })
              }
            />
            {errors.shortDesc && (
              <p className="text-red-600 text-sm mt-1">{errors.shortDesc}</p>
            )}
          </div>

          {/* Full Description with Height */}
          <div>
            <label className="font-semibold text-gray-700">
              Full Description
            </label>
            <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden">
              <CKEditor
                editor={ClassicEditor}
                data={formData.fullDesc}
                onChange={(event, editor) =>
                  setFormData({ ...formData, fullDesc: editor.getData() })
                }
                config={{
                  height: 500,
                  toolbar: {
                    items: [
                      "heading", "|",
                      "bold", "italic", "link", "bulletedList", "numberedList", "|",
                      "outdent", "indent", "|",
                      "imageUpload", "blockQuote", "insertTable", "mediaEmbed", "undo", "redo"
                    ]
                  }
                }}
              />
            </div>
            {errors.fullDesc && (
              <p className="text-red-600 text-sm mt-1">{errors.fullDesc}</p>
            )}
          </div>

          {/* Submit */}
          <div className="pt-4 flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-3 bg-[var(--primary-color)] text-white rounded-lg hover:bg-[var(--primary-hover)] shadow disabled:opacity-70"
            >
              {isLoading ? "Saving..." : isEditMode ? "Update Blog" : "Save Blog"}
            </button>
          </div>
        </form>
      </div>

      <SuccesModal
        open={success}
        onClose={() => setSuccess(false)}
        title={isEditMode ? "Blog Updated!" : "Blog Added Successfully!"}
        subtitle="Blog has been saved."
        autoClose={true}
        autoCloseTime={2500}
      />
    </MasterLayout>
  );
}